import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import "./profile.scss";
import LoadingSpinner from "../loader/Loader";
import {
  useGetMeQuery,
  useUpdateProfileMutation,
} from "../../features/api/userApiSlice";
import { useVideo } from "../../hooks/useVideo";

import { useRecaptcha, executeRecaptcha } from "../../hooks/useRecaptcha";

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Validation schema using Yup
const validationSchema = Yup.object({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email address").required("Required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain a lowercase letter")
    .matches(/[A-Z]/, "Password must contain an uppercase letter")
    .matches(/[0-9]/, "Password must contain a number")
    .matches(/[\W_]/, "Password must contain a special character"),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "Passwords must match"
  ),
});

const Profile = () => {
  const { formatPhoneNumber } = useVideo();
  const { data: user, isLoading, isError, error } = useGetMeQuery();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  useRecaptcha(SITE_KEY, ["/profile"]); // ✅ only show badge on /profile

  const initialValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    username: user?.username || "",
    phoneNumber: user?.phoneNumber || "",
    password: "",
    currentPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      username: values.username,
      email: values.email,
      phoneNumber: values.phoneNumber,
    };
    if (showPasswordFields && values.password && values.currentPassword) {
      payload.password = values.password;
      payload.currentPassword = values.currentPassword;
    }

    try {
      const token = await executeRecaptcha(SITE_KEY, "profile");
      const res = await updateProfile({
        ...payload,
        recaptchaToken: token,
      }).unwrap();
      toast.success(res?.message || "Profile updated successfully!");
      setIsEditMode(false);
      setShowPasswordFields(false);
      resetForm({
        values: {
          ...values,
          password: "",
          confirmPassword: "",
          currentPassword: "",
        },
      });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update profile!");
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return <div className="error-message">Error: {error.message}</div>;
  if (!user) return <div className="info-message">User not found.</div>;

  return (
    <div className="main_wrapper">
      <div className="page-container">
        <div className="user-profile">
          <h2>Your Profile</h2>
          <div className="profile-header">
            <h3>
              {user.firstName} {user.lastName}
            </h3>
            <p>{user.email}</p>
            <div className="profile-actions">
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className="btn-edit"
              >
                {isEditMode ? "Cancel" : "Edit Profile"}
              </button>
            </div>
          </div>

          <div className="profile-details-container">
            {/* CONDITIONAL RENDERING: Only show this section if NOT in edit mode */}
            {!isEditMode && (
              <div className="profile-info-section">
                <h4>Account Information</h4>
                <p>
                  <strong>Subscription Plan:</strong>{" "}
                  {user.subscription?.isActive
                    ? user.subscription.plan
                    : "None"}
                </p>
                <p>
                  <strong>Roles:</strong> {user.roles.join(", ")}
                </p>
                <p>
                  <strong>Verified:</strong> {user.isVerified ? "Yes" : "No"}
                </p>
              </div>
            )}

            {isEditMode && (
              <div className="profile-edit-section">
                <h4>Edit Profile</h4>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ isSubmitting, setFieldValue }) => (
                    <Form>
                      <div className="form-group">
                        <label htmlFor="username">User Name</label>
                        <Field name="username" type="text" />
                        <ErrorMessage
                          name="username"
                          component="div"
                          className="error"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="firstName">First Name</label>
                        <Field name="firstName" type="text" />
                        <ErrorMessage
                          name="firstName"
                          component="div"
                          className="error"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name</label>
                        <Field name="lastName" type="text" />
                        <ErrorMessage
                          name="lastName"
                          component="div"
                          className="error"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <Field name="email" type="email" />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="error"
                        />
                      </div>
                      {/* phoneNumber */}
                      <div className="form-controls">
                        <label htmlFor="phoneNumber">Phone Number</label>
                        <Field name="phoneNumber">
                          {({ field }) => (
                            <Field
                              {...field}
                              type="text"
                              placeholder="515-635-8946"
                              maxLength={12}
                              onChange={(e) => {
                                const formattedPhone = formatPhoneNumber(
                                  e.target.value
                                );
                                setFieldValue("phoneNumber", formattedPhone);
                              }}
                              aria-required="true"
                              aria-describedby="phoneNumberError"
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="phoneNumber"
                          component="div"
                          className="error"
                          id="phoneNumberError"
                        />
                      </div>

                      {showPasswordFields && (
                        <>
                          <div className="form-group">
                            <label htmlFor="currentPassword">
                              Current Password
                            </label>
                            <Field name="currentPassword" type="password" />
                            <ErrorMessage
                              name="currentPassword"
                              component="div"
                              className="error"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="password">New Password</label>
                            <Field name="password" type="password" />
                            <ErrorMessage
                              name="password"
                              component="div"
                              className="error"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="confirmPassword">
                              Confirm Password
                            </label>
                            <Field name="confirmPassword" type="password" />
                            <ErrorMessage
                              name="confirmPassword"
                              component="div"
                              className="error"
                            />
                          </div>
                        </>
                      )}
                      <div className="profile-action-flex">
                        <button
                          type="button"
                          onClick={() =>
                            setShowPasswordFields(!showPasswordFields)
                          }
                          className="btn-toggle-password"
                        >
                          {showPasswordFields
                            ? "Hide Password Fields"
                            : "Change Password"}
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || isUpdating}
                          className="btn-save"
                        >
                          {isUpdating || isSubmitting
                            ? "Saving..."
                            : "Save Changes"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

{
  /* <h3>Important Information:</h3>
        <p>
          Deleting your account will permanently remove all your data, including
          your my list, comments, and any saved preferences.
        </p>
        <p>
          This action cannot be undone. If you ever wish to return to the
          community, you will need to create a new account.
        </p>
        <p>
          If you still wish to proceed with the account deletion, please click
          the confirmation button below:
        </p> */
}
